import {
  type SelectChangeEvent,
  Stack,
  //Switch,
  //SwitchProps,
  styled,
  RadioGroup,
  FormControlLabel,
  Radio,
  RadioProps,
  Checkbox,
  checkboxClasses,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft'
import InfoTooltip from '../../InfoTooltip'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import type { Definition } from '../../../types'
import MultipleSelect from './MultipleSelect'
import Button from '../../shared/Button'
import { Color } from '../../../constants'
import { FILTER_BUTTON_STYLES } from './constants'

const CustomRadio = styled((props: RadioProps) => (
  <Radio focusRipple={false} {...props} />
))(() => ({
  color: '#E9E7E1',
  padding: '2px 9px',
  '&.Mui-focusVisible': {
    outline: 'none',
  },
  '&.Mui-checked': {
    color: '#E9E7E1',
  },
}))

// const CustomSwitch = styled((props: SwitchProps) => (
//   <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
// ))(({ theme }) => ({
//   width: 42,
//   height: 23,
//   padding: 0,
//   '& .MuiSwitch-switchBase': {
//     padding: 0,
//     margin: 4,
//     transitionDuration: '300ms',
//     '&.Mui-checked': {
//       transform: 'translateX(16px)',
//       color: '#fff',
//       paddingLeft: 4,
//       '& + .MuiSwitch-track': {
//         backgroundColor: '#646464',
//         opacity: 1,
//         border: 0,
//       },
//       '&.Mui-disabled + .MuiSwitch-track': {
//         opacity: 0.5,
//       },
//     },
//     '&.Mui-focusVisible .MuiSwitch-thumb': {
//       color: '#222222',
//       border: '6px solid #222222',
//     },
//     '&.Mui-disabled .MuiSwitch-thumb': {
//       color: '#222222',
//     },
//     '&.Mui-disabled + .MuiSwitch-track': {
//       opacity: 0.7,
//     },
//   },
//   '& .MuiSwitch-thumb': {
//     boxSizing: 'border-box',
//     width: 15,
//     height: 15,
//     color: '#e9e7e1',
//   },
//   '& .MuiSwitch-track': {
//     borderRadius: 11,
//     backgroundColor: '#222222',
//     opacity: 1,
//     transition: theme.transitions.create(['background-color'], {
//       duration: 500,
//     }),
//     border: `1px solid #e9e7e1`,
//     width: '100%',
//   },
// }))

type FilterSectionProps = {
  display: string
  setDisplay: Dispatch<SetStateAction<string>>
  handleDisplay: (value: string) => void
  definitions: Definition[]
  selectedGeoScopeOptions: string[]
  onSelectedGeoScopeOptionsChange: Dispatch<SetStateAction<string[]>>
  selectedFocusOptions: string[]
  onSelectedFocusOptionsChange: Dispatch<SetStateAction<string[]>>
  selectedStrategyOptions: string[]
  onSelectedStrategyOptionsChange: Dispatch<SetStateAction<string[]>>
  liableInstitution: string
  onLiableInstitutionChange: Dispatch<SetStateAction<string>>
  isInitCommTask: boolean
  onInitCommTaskChange: (checked: boolean) => void
  selectedStageOptions: string[]
  onSelectedStageOptionsChange: Dispatch<SetStateAction<string[]>>
  selectedRepairOptions: string[]
  onSelectedRepairOptionsChange: Dispatch<SetStateAction<string[]>>
  selectedInitiativeTypeCheckboxes: { [key: string]: boolean }
  onSelectedInitiativeTypeCheckboxesChange: Dispatch<
    SetStateAction<{ [key: string]: boolean }>
  >
  selectedInitiativeTypeOptions: string[]
  onSelectedInitiativeTypeOptionsChange: Dispatch<SetStateAction<string[]>>
  filterSectionRef: React.RefObject<HTMLDivElement>
  mapHeight: number
}

function FilterSection({
  display,
  setDisplay,
  handleDisplay,
  definitions,
  selectedGeoScopeOptions,
  onSelectedGeoScopeOptionsChange,
  selectedFocusOptions,
  onSelectedFocusOptionsChange,
  selectedStrategyOptions,
  onSelectedStrategyOptionsChange,
  liableInstitution,
  onLiableInstitutionChange,
  isInitCommTask,
  onInitCommTaskChange,
  selectedStageOptions,
  onSelectedStageOptionsChange,
  selectedRepairOptions,
  onSelectedRepairOptionsChange,
  selectedInitiativeTypeCheckboxes,
  onSelectedInitiativeTypeCheckboxesChange,
  selectedInitiativeTypeOptions,
  onSelectedInitiativeTypeOptionsChange,
  filterSectionRef,
  mapHeight,
}: FilterSectionProps) {
  // Get menu options from the definitions file
  const geoScopeOptions = definitions.filter((def) =>
    def.id.startsWith('geo_scope:')
  )
  const focusOptions = definitions.filter((def) => def.id.startsWith('focus:'))

  const strategyOptions = definitions.filter((def) =>
    def.id.startsWith('strategy:')
  )

  const stageOptions = definitions.filter((def) => def.id.startsWith('stage:'))

  const repairOptions = definitions.filter((def) =>
    def.id.startsWith('repair:')
  )

  const initiativeTypeOptions = definitions.filter((def) =>
    def.id.startsWith('initiative_type:')
  )

  const handleGeoScopeChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event
    // On autofill we get a stringified value.
    const newValue = typeof value === 'string' ? value.split(',') : value
    onSelectedGeoScopeOptionsChange(newValue)
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'filter_applied', {
        filter_type: 'geo_scope',
        filter_value: newValue.join(', '),
      })
    }
  }

  const handleFocusAreaChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event
    // On autofill we get a stringified value.
    const newValue = typeof value === 'string' ? value.split(',') : value
    onSelectedFocusOptionsChange(newValue)
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'filter_applied', {
        filter_type: 'focus_area',
        filter_value: newValue.join(', '),
      })
    }
  }

  const handleStrategyChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event
    // On autofill we get a stringified value.
    const newValue = typeof value === 'string' ? value.split(',') : value
    onSelectedStrategyOptionsChange(newValue)
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'filter_applied', {
        filter_type: 'strategy',
        filter_value: newValue.join(', '),
      })
    }
  }

  // const handleIsInitCommTaskChange = (
  //   _event: ChangeEvent<HTMLInputElement>,
  //   checked: boolean
  // ) => {
  //   onInitCommTaskChange(checked)
  // }

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = (event.target as HTMLInputElement).value
    onLiableInstitutionChange(newValue)
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'filter_applied', {
        filter_type: 'liable_institution',
        filter_value: newValue,
      })
    }
  }

  const handleStageChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event
    // On autofill we get a stringified value.
    const newValue = typeof value === 'string' ? value.split(',') : value
    onSelectedStageOptionsChange(newValue)
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'filter_applied', {
        filter_type: 'stage',
        filter_value: newValue.join(', '),
      })
    }
  }

  const handleRepairChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event
    // On autofill we get a stringified value.
    const newValue = typeof value === 'string' ? value.split(',') : value
    onSelectedRepairOptionsChange(newValue)
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'filter_applied', {
        filter_type: 'repair',
        filter_value: newValue.join(', '),
      })
    }
  }

  const handleInitiativeTypeChange = (event: any) => {
    onSelectedInitiativeTypeCheckboxesChange({
      ...selectedInitiativeTypeCheckboxes,
      [event.target.name]: event.target.checked,
    })
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'filter_applied', {
        filter_type: 'initiative_type',
        filter_value: event.target.name,
        filter_checked: event.target.checked,
      })
    }
  }

  useEffect(() => {
    const array = Object.entries(selectedInitiativeTypeCheckboxes)
      .filter(([key, value]) => value === true)
      .map(([key]) => key)
    onSelectedInitiativeTypeOptionsChange(array)
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInitiativeTypeCheckboxes])

  const showBoth = display === 'all'
  const showOrgs = display === 'orgs'
  const showInits = display === 'inits'

  const mainColor: string = '#E9E7E1'
  const checkmarkSvg = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
    <path fill-rule="evenodd" clip-rule="evenodd"
      d="M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z"
      fill="${mainColor}"/>
  </svg>
`
  const encodedCheckmarkSvg = encodeURIComponent(checkmarkSvg)
  return (
    <div
      className="py-6 pl-4 pr-8 bg-filter-section-bg w-[285px] h-[504px] absolute left-0 bg-right"
      style={{
        top: `${(mapHeight - 504) / 2}px`,
      }}
      ref={filterSectionRef}
    >
      {showBoth && (
        <Stack className="gap-3 justify-center items-center h-full pb-8">
          <p className="font-decimal text-[17px] text-white font-semibold text-center">
            Select Reparations Movement
          </p>
          <Button
            onClick={() => {
              if (typeof window.gtag === 'function') {
                window.gtag('event', 'filter_category_selected', { category: 'initiatives' })
              }
              setDisplay('inits')
            }}
            textColor={Color.White}
            backgroundColor={Color.Black}
            styles={FILTER_BUTTON_STYLES}
          >
            <Stack direction="row" className="items-center">
              <div className="rounded-full flex bg-mediumyellow min-w-[12px] max-w-[12px] h-[12px] mr-2"></div>
              <span>Initiatives</span>
            </Stack>
          </Button>

          <p className="font-decimal text-[14px] text-white font-semibold">
            or
          </p>
          <Button
            onClick={() => {
              if (typeof window.gtag === 'function') {
                window.gtag('event', 'filter_category_selected', { category: 'movement_organizations' })
              }
              setDisplay('orgs')
            }}
            textColor={Color.White}
            backgroundColor={Color.Black}
            styles={FILTER_BUTTON_STYLES}
          >
            <Stack direction="row" className="items-center">
              <div className="rounded-full flex bg-green min-w-[12px] max-w-[12px] h-[12px] mr-2"></div>
              <span>Movement Organizations</span>
            </Stack>
          </Button>
        </Stack>
      )}
      {showOrgs && (
        <>
          <Stack className="gap-5 text-white">
            <button
              className="font-decimal text-[12px] bg-transparent border border-solid border-white flex justify-center items-center px-2 py-1 cursor-pointer w-max "
              type="button"
              onClick={() => handleDisplay('all')}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="text-white" />
              <p className="ml-1">BACK</p>
            </button>
            <p className="text-[16px] uppercase font-semibold mt-2">
              Movement Orgs
            </p>

            <MultipleSelect
              placeholder={
                definitions
                  .find((def) => def.id === 'geo_scope')
                  ?.display_label.toUpperCase() || ''
              }
              options={geoScopeOptions}
              selectedOptions={selectedGeoScopeOptions}
              description={
                definitions.find((def) => def.id === 'geo_scope')?.definition ||
                ''
              }
              onChange={handleGeoScopeChange}
              onChipClick={(option) => {
                onSelectedGeoScopeOptionsChange((prevState) => {
                  return prevState.filter((item) => item !== option)
                })
              }}
            />
            <MultipleSelect
              placeholder={
                definitions
                  .find((def) => def.id === 'focus')
                  ?.display_label.toUpperCase() || ''
              }
              options={focusOptions}
              selectedOptions={selectedFocusOptions}
              description={
                definitions.find((def) => def.id === 'focus')?.definition || ''
              }
              onChange={handleFocusAreaChange}
              onChipClick={(option) => {
                onSelectedFocusOptionsChange((prevState) => {
                  return prevState.filter((item) => item !== option)
                })
              }}
            />
            <MultipleSelect
              placeholder={
                definitions
                  .find((def) => def.id === 'strategy')
                  ?.display_label.toUpperCase() || ''
              }
              options={strategyOptions}
              selectedOptions={selectedStrategyOptions}
              description={
                definitions.find((def) => def.id === 'strategy')?.definition ||
                ''
              }
              onChange={handleStrategyChange}
              onChipClick={(option) => {
                onSelectedStrategyOptionsChange((prevState) => {
                  return prevState.filter((item) => item !== option)
                })
              }}
            />
            <Stack>
              <Stack direction="row" className="items-center gap-2 mb-2">
                <p className="font-decimal text-[16px] text-white font-semibold">
                  {definitions
                    .find((def) => def.id === 'target')
                    ?.display_label.toUpperCase() || ''}
                </p>
                <InfoTooltip
                  title={
                    definitions.find((def) => def.id === 'target')
                      ?.definition || ''
                  }
                />
              </Stack>
              <RadioGroup
                aria-labelledby="radio-buttons-group-label"
                defaultValue="all"
                name="radio-buttons-group"
                className="font-decimal text-white text-[14px] leading-[14px]"
                onChange={handleRadioChange}
                value={liableInstitution}
              >
                <FormControlLabel
                  value="all"
                  control={<CustomRadio />}
                  label="All"
                />
                <FormControlLabel
                  value="government"
                  control={<CustomRadio />}
                  label="Government"
                />
                <FormControlLabel
                  value="institutions"
                  control={<CustomRadio />}
                  label="Institutions"
                />
              </RadioGroup>
            </Stack>
          </Stack>
        </>
      )}
      {showInits && (
        <>
          <Stack className="gap-5  text-white">
            <button
              className="font-decimal text-[12px] bg-transparent border border-solid border-white flex justify-center items-center px-2 py-1 cursor-pointer w-max "
              type="button"
              onClick={() => handleDisplay('all')}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="text-white" />
              <p className="ml-1">BACK</p>
            </button>
            <p className="text-[16px] uppercase font-semibold mt-2">
              Initiatives
            </p>

            <MultipleSelect
              placeholder={
                definitions
                  .find((def) => def.id === 'stage')
                  ?.display_label.toUpperCase() || ''
              }
              options={stageOptions}
              selectedOptions={selectedStageOptions}
              description={
                definitions.find((def) => def.id === 'stage')?.definition || ''
              }
              onChange={handleStageChange}
              onChipClick={(option) => {
                onSelectedStageOptionsChange((prevState) => {
                  return prevState.filter((item) => item !== option)
                })
              }}
            />
            <MultipleSelect
              placeholder={
                definitions
                  .find((def) => def.id === 'repair')
                  ?.display_label.toUpperCase() || ''
              }
              options={repairOptions}
              selectedOptions={selectedRepairOptions}
              description={
                definitions.find((def) => def.id === 'repair')?.definition || ''
              }
              onChange={handleRepairChange}
              onChipClick={(option) => {
                onSelectedRepairOptionsChange((prevState) => {
                  return prevState.filter((item) => item !== option)
                })
              }}
            />
            <Stack>
              <Stack direction="row" className="items-center mb-2">
                <p className="font-decimal text-[16px] text-white font-semibold mr-2">
                  {definitions
                    .find((def) => def.id === 'initiative_type')
                    ?.display_label.toUpperCase() || ''}
                </p>
                <InfoTooltip
                  title={
                    definitions.find((def) => def.id === 'initiative_type')
                      ?.definition || ''
                  }
                />
              </Stack>
              {initiativeTypeOptions.map((option) => {
                return (
                  <FormControlLabel
                    key={option.id}
                    control={
                      <Checkbox
                        disableRipple
                        onChange={handleInitiativeTypeChange}
                        checked={
                          selectedInitiativeTypeCheckboxes[
                            option.id.split(':')[1]
                          ]
                        }
                        checkedIcon={<span />}
                        icon={<span />}
                        name={option.id.split(':')[1]}
                        sx={{
                          [`&:not(.${checkboxClasses.checked})`]: {
                            '& input + *': {
                              boxShadow: `inset 0 0 0 1px ${mainColor}, inset 0 -1px 0 ${mainColor}`,
                              backgroundColor: '#212121',
                              width: '16px',
                              height: '16px',
                            },
                          },
                          [`&.${checkboxClasses.checked}`]: {
                            '& input + *': {
                              boxShadow: `inset 0 0 0 1px ${mainColor}, inset 0 -1px 0 ${mainColor}`,
                              backgroundColor: '#212121',
                              backgroundImage:
                                'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
                              '&:before': {
                                display: 'block',
                                width: '16px',
                                height: '16px',
                                backgroundImage: `url("data:image/svg+xml,${encodedCheckmarkSvg}")`,
                                content: '""',
                              },
                            },
                          },
                        }}
                      />
                    }
                    label={option.display_label}
                    sx={{
                      marginLeft: 0,
                      '& .MuiFormControlLabel-label': {
                        fontSize: '14px',
                        color: 'white',
                      },
                    }}
                  />
                )
              })}
            </Stack>
          </Stack>
        </>
      )}
    </div>
  )
}

export default FilterSection

//OLD CODE FOR InitCommTask toggle
// <Stack className="gap-4">
//         <Stack direction="row" className="items-start">
//           <p className="text-[16px] text-white font-semibold font-decimal w-[150px]">
//             {definitions
//               .find((def) => def.id === 'initiative_commission_taskforce')
//               ?.display_label.toUpperCase() || ''}
//           </p>
//           <div className="mt-1">
//             <InfoTooltip
//               title={
//                 definitions.find(
//                   (def) => def.id === 'initiative_commission_taskforce'
//                 )?.definition || ''
//               }
//               customWidth
//             />
//           </div>
//         </Stack>
//         <CustomSwitch
//           checked={isInitCommTask}
//           onChange={handleIsInitCommTaskChange}
//         />
//       </Stack>
